import { Group, Paper, Progress, Text, ThemeIcon } from '@mantine/core';

// Placeholder for "we have no value here" — used instead of 'N/A' so an empty row
// stays visually quiet next to the rows that do carry a value.
export const DASH = '—';

// Compact "20 Feb 2026, 14:00". The shared `formatDate` helper spells the month out
// and includes seconds, which wraps onto two lines in the narrow overview panels.
export const formatDateTime = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString(navigator.language, { dateStyle: 'medium', timeStyle: 'short' });
};

export const formatDay = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(navigator.language, { dateStyle: 'medium' });
};

// A titled panel — the building block of the profile overview grid. `h="100%"` makes
// every panel on the same grid row share the height of the tallest one.
export const Panel = ({ icon: Icon, title, action, children }) => (
  <Paper withBorder radius="md" p="md" h="100%">
    <Group justify="space-between" wrap="nowrap" gap="xs" mb="sm">
      <Group gap="xs" wrap="nowrap">
        {Icon && (
          <ThemeIcon size="sm" radius="sm" variant="light" color="gray">
            <Icon size={14} />
          </ThemeIcon>
        )}
        <Text size="xs" fw={700} tt="uppercase" c="dimmed">
          {title}
        </Text>
      </Group>
      {action}
    </Group>
    {children}
  </Paper>
);

// Dimmed label on the left, value right-aligned. Long values (emails) break rather
// than push the label out of the panel.
export const InfoRow = ({ label, hint, children }) => (
  <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
    <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
      {label}
    </Text>
    <div style={{ minWidth: 0, textAlign: 'right' }}>
      <Text size="sm" component="div" style={{ overflowWrap: 'anywhere' }}>
        {children}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </div>
  </Group>
);

// A single ratio against a limit. The fill carries the severity and the track is a
// lighter step of the same ramp, so the state reads across the whole bar instead of
// only across the filled part.
export const Meter = ({ value, color = 'gray', ...others }) => (
  <Progress
    value={value ?? 0}
    color={color}
    size="sm"
    radius="xl"
    styles={{ root: { backgroundColor: `var(--mantine-color-${color}-1)` } }}
    {...others}
  />
);

// KPI tile: label, the value in text ink (never the status colour — the meter and the
// icon carry that), an optional meter for ratios, and a hint that names the status in
// words so the colour is never the only signal.
export const StatTile = ({ icon: Icon, label, value, color = 'gray', meter, badge, hint }) => (
  <Paper withBorder radius="md" p="sm" h="100%">
    <Group justify="space-between" wrap="nowrap" gap="xs">
      <Group gap={6} wrap="nowrap">
        {Icon && (
          <ThemeIcon size={20} radius="sm" variant="light" color={color}>
            <Icon size={13} />
          </ThemeIcon>
        )}
        <Text size="xs" fw={600} tt="uppercase" c="dimmed">
          {label}
        </Text>
      </Group>
      {badge}
    </Group>
    <Text fz={22} fw={600} lh={1.3} mt={6} style={{ overflowWrap: 'anywhere' }}>
      {value}
    </Text>
    {typeof meter === 'number' && <Meter value={meter} color={color} mt={8} />}
    {hint && (
      <Text size="xs" c="dimmed" mt={6}>
        {hint}
      </Text>
    )}
  </Paper>
);
